import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  clientActionSubscribe,
  clientActionUnsubscribe,
  EntityTypeEnum,
  SubscribeType,
  SubscriptionFilter,
  SubscriptionIdentifier
} from 'api'
import {
  DefaultSubscriptionFilters,
  PersistentSubscriptionFilters,
  PostTransformSubscriptionFilters,
  SubscriptionFilterConfig,
  updateWithStateFilters
} from 'model'
import { userGlobalGridSettingsSelector } from 'selectors/gridSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { isCurrentOrganisationBuySideSelector } from 'selectors/organisationSelectors'
import { getPageSize } from 'utils/pageSizeUtils'
import { marketDetailsSelector } from 'selectors/platformSelectors'

const authorisedUserSelector = createAuthorisedUserSelector()

export const useChangedSubscriptionFilters = (
  entityTypeEnum: EntityTypeEnum,
  identifier: SubscriptionIdentifier,
  paged: boolean = false
) => {
  const dispatch = useDispatch()
  const globalGridSettings = useSelector(userGlobalGridSettingsSelector)
  const marketDetails = useSelector(marketDetailsSelector)
  const authorisedUser = useSelector(authorisedUserSelector)
  const isBuySide = useSelector(isCurrentOrganisationBuySideSelector)

  const filterConfig: SubscriptionFilterConfig = useMemo(
    () => ({
      marketDetails,
      globalGridSettings,
      authorisedUser,
      isBuySide
    }),
    [authorisedUser, globalGridSettings, marketDetails, isBuySide]
  )

  return useCallback(
    (filters: SubscriptionFilter[] | null) => {
      if (filters === null) {
        dispatch(
          clientActionSubscribe({
            entityTypeEnum,
            preDefinedFilter: null,
            filters: updateWithStateFilters(DefaultSubscriptionFilters[identifier], identifier, filterConfig),
            postTransformFilters: PostTransformSubscriptionFilters[identifier],
            subscribeType: paged ? SubscribeType.PAGE : SubscribeType.FILTERED,
            subscriptionIdentifier: identifier,
            limitResults: false,
            pageSize: paged ? getPageSize(identifier) : null,
            pageNumber: paged ? 0 : null,
            summaryRequest: null
          })
        )
      } else if (filters.length === 0) {
        dispatch(
          clientActionUnsubscribe({
            entityTypeEnum,
            subscriptionIdentifier: identifier
          })
        )
      } else {
        dispatch(
          clientActionSubscribe({
            entityTypeEnum,
            preDefinedFilter: null,
            filters: updateWithStateFilters([...PersistentSubscriptionFilters[identifier], ...filters], identifier, filterConfig),
            postTransformFilters: PostTransformSubscriptionFilters[identifier],
            subscribeType: paged ? SubscribeType.PAGE : SubscribeType.FILTERED,
            subscriptionIdentifier: identifier,
            limitResults: !paged,
            pageSize: paged ? getPageSize(identifier) : null,
            pageNumber: paged ? 0 : null,
            summaryRequest: null
          })
        )
      }
    },
    [dispatch, entityTypeEnum, identifier, filterConfig, paged]
  )
}
